import { Children, type ReactNode } from "react";

import { Card, Heading, LinkButton, TagGroup } from "@stacklet/ui";

import { IdentityProviderLogo } from "app/components/rbac/IdentityProviderLogo";

/** Props for the IdentityProviderCard component */
interface Props {
  /** The provider's `idpType`, which picks the logo */
  idpType: string;
  /** The provider's display name, or its identifier when it has none */
  title: string;
  /** Stable identifier for the provider, shown beside the title */
  identifier: string;
  /** The provider's stable namespace */
  namespace: string;
  /** Where "View Details" leads */
  detailsPath: string;
  /**
   * Capability tags for the provider. A list rather than a fragment so the
   * ones that don't apply can be passed as null and counted out — a provider
   * may grant none, and an empty tag group is a labelled region with nothing
   * in it.
   */
  tags: ReactNode[];
}

/**
 * The card both kinds of identity provider are listed as: the provider's logo
 * and name, its identifier and stable namespace, the capabilities it grants,
 * and a link to its details.
 *
 * What fills those slots is what differs by kind — a SAML provider is named and
 * addressed by its Cognito provider name, an organization by its key — so each
 * kind has its own component to read its own fields, and this holds only the
 * chrome they share. The details pages are arranged the same way, around
 * {@link IdentityProviderDetailsHeader}.
 */
export function IdentityProviderCard({
  idpType,
  title,
  identifier,
  namespace,
  detailsPath,
  tags,
}: Props) {
  // Drops the tags that don't apply, and keys what's left.
  const shownTags = Children.toArray(tags);

  return (
    <Card className="flex flex-col gap-y-sm border border-overlay-medium p-sm">
      <div className="flex items-center justify-between gap-md">
        <div className="flex min-w-0 items-center gap-x-xs">
          <IdentityProviderLogo
            className="shrink-0"
            idpType={idpType}
            size={24}
          />
          <Heading className="truncate" level={3}>
            {title}
          </Heading>
          {/* A provider with no display name is titled by its identifier
              already, so repeating it would just be noise. */}
          {identifier === title ? null : (
            <span className="truncate text-label-small text-text-secondary">
              {identifier}
            </span>
          )}
          <span className="shrink-0 font-mono text-label-extra-small text-text-secondary">
            {namespace}
          </span>
        </div>

        <LinkButton className="shrink-0" href={detailsPath} variant="tertiary">
          View Details
        </LinkButton>
      </div>

      {shownTags.length > 0 ? (
        <TagGroup aria-label={`${title} capabilities`} tagListClassName="flex">
          {shownTags}
        </TagGroup>
      ) : null}
    </Card>
  );
}
